import { Fragment, useState } from 'react';
import {
  Box,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import { useTables } from '../../hooks/useMySQLSchema';
import type { MySQLDatabase } from '../../api/mysql';

interface Props {
  databases: MySQLDatabase[];
  selectedDb: string | null;
  selectedTable: string | null;
  onSelectTable: (db: string, table: string) => void;
}

function DbNode({ db, selectedDb, selectedTable, onSelectTable }: {
  db: MySQLDatabase;
  selectedDb: string | null;
  selectedTable: string | null;
  onSelectTable: (db: string, table: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const { data: tables, isLoading } = useTables(expanded ? db.name : null);

  return (
    <Fragment>
      <ListItemButton onClick={() => setExpanded((e) => !e)} sx={{ py: 0.5 }}>
        <ListItemText
          primary={`🗄 ${db.name}`}
          primaryTypographyProps={{
            variant: 'body2',
            fontFamily: 'monospace',
            fontWeight: selectedDb === db.name ? 700 : 400,
            noWrap: true,
          }}
        />
        {expanded
          ? <ExpandLessIcon fontSize="small" sx={{ flexShrink: 0, color: 'text.secondary' }} />
          : <ExpandMoreIcon fontSize="small" sx={{ flexShrink: 0, color: 'text.secondary' }} />
        }
      </ListItemButton>

      {expanded && (
        isLoading ? (
          <Box sx={{ pl: 3, py: 0.75 }}><CircularProgress size={14} /></Box>
        ) : tables?.length === 0 ? (
          <Typography variant="caption" sx={{ pl: 3, py: 0.5, display: 'block', color: 'text.secondary' }}>
            No tables
          </Typography>
        ) : (
          tables?.map((t) => (
            <ListItemButton
              key={t}
              selected={selectedDb === db.name && selectedTable === t}
              onClick={() => onSelectTable(db.name, t)}
              sx={{ pl: 3, py: 0.25 }}
            >
              <ListItemText
                primary={`≡ ${t}`}
                primaryTypographyProps={{ variant: 'caption', fontFamily: 'monospace', noWrap: true }}
              />
            </ListItemButton>
          ))
        )
      )}
    </Fragment>
  );
}

export function DbTreeSidebar({ databases, selectedDb, selectedTable, onSelectTable }: Props) {
  const [search, setSearch] = useState('');
  const filtered = search.trim()
    ? databases.filter((db) => db.name.toLowerCase().includes(search.toLowerCase()))
    : databases;

  return (
    <Box sx={{ width: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider' }}>
      <Box sx={{ p: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Filter…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List dense disablePadding>
          {filtered.length === 0 && (
            <Typography variant="caption" sx={{ p: 1, display: 'block', color: 'text.secondary' }}>
              No matches
            </Typography>
          )}
          {filtered.map((db) => (
            <DbNode
              key={db.name}
              db={db}
              selectedDb={selectedDb}
              selectedTable={selectedTable}
              onSelectTable={onSelectTable}
            />
          ))}
        </List>
      </Box>
    </Box>
  );
}
